/**
 * Enhanced fetch wrapper that automatically adds Bearer token to requests
 * and handles token refresh on 401 errors
 */

const TOKEN_STORAGE_KEY = "centil_access_token";
const REFRESH_TOKEN_STORAGE_KEY = "centil_refresh_token";
// Direct connection to auth service (CORS is configured)
const AUTH_BASE_URL = "https://capps-auth-2lg2zbrl7p7b6.ashyhill-1451cba1.swedencentral.azurecontainerapps.io/api/auth";

interface AuthFetchOptions extends RequestInit {
    skipAuth?: boolean; // Skip adding auth header
}

let isRefreshing = false;
let failedQueue: Array<{ resolve: (value?: any) => void; reject: (reason?: any) => void }> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });

    failedQueue = [];
};

const refreshAccessToken = async (): Promise<string> => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);

    if (!refreshToken) {
        throw new Error("No refresh token available");
    }

    const response = await fetch(`${AUTH_BASE_URL}/refresh`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ refresh: refreshToken })
    });

    if (!response.ok) {
        // Refresh token expired - clear everything and redirect to login
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
        localStorage.removeItem("centil_user");
        window.location.href = "/#/login";
        throw new Error("Token refresh failed");
    }

    const data = await response.json();

    // Update tokens
    localStorage.setItem(TOKEN_STORAGE_KEY, data.access);
    if (data.refresh) {
        localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, data.refresh);
    }

    return data.access;
};

export const authFetch = async (url: string, options: AuthFetchOptions = {}): Promise<Response> => {
    const { skipAuth, ...fetchOptions } = options;

    // Add Bearer token if not skipping auth
    if (!skipAuth) {
        const accessToken = localStorage.getItem(TOKEN_STORAGE_KEY);
        if (accessToken) {
            fetchOptions.headers = {
                ...fetchOptions.headers,
                Authorization: `Bearer ${accessToken}`
            };
        }
    }

    let response = await fetch(url, fetchOptions);

    // If 401 and not already refreshing, try to refresh token
    if (response.status === 401 && !skipAuth) {
        if (isRefreshing) {
            // Wait for the refresh to complete
            return new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject });
            }).then(token => {
                fetchOptions.headers = {
                    ...fetchOptions.headers,
                    Authorization: `Bearer ${token}`
                };
                return fetch(url, fetchOptions);
            });
        }

        isRefreshing = true;

        try {
            const newToken = await refreshAccessToken();
            isRefreshing = false;
            processQueue(null, newToken);

            // Retry original request with new token
            fetchOptions.headers = {
                ...fetchOptions.headers,
                Authorization: `Bearer ${newToken}`
            };
            response = await fetch(url, fetchOptions);
        } catch (error) {
            isRefreshing = false;
            processQueue(error as Error, null);
            throw error;
        }
    }

    return response;
};
